import styled, { keyframes } from "styled-components";
import { usePage } from "@inertiajs/react";
import { useLandingLanguage } from "../../../contexts/landing-language";

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const BookingSection = styled.section`
  background: #faf8f5;
  padding: 100px 24px;
  position: relative;
  z-index: 10;
`;

const BookingContainer = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  background: #ffffff;
  border-radius: 4px;
  padding: 50px 60px;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.1);
  animation: ${fadeInUp} 0.8s ease forwards;

  @media (max-width: 768px) {
    padding: 32px 24px;
    margin-top: -40px;
  }
`;

const BookingTitle = styled.h2`
  font-size: 13px;
  font-weight: 600;
  color: #c9a86c;
  margin-bottom: 8px;
  text-align: center;
  letter-spacing: 3px;
  text-transform: uppercase;
`;

const BookingSubtitle = styled.p`
  font-size: 32px;
  font-weight: 300;
  color: #1a1a1a;
  margin-bottom: 40px;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 26px;
  }
`;

const OtaList = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const OtaLink = styled.a`
  border: 1px solid #e5e1dc;
  border-radius: 8px;
  padding: 18px;
  text-decoration: none;
  color: #1a1a1a;
  display: flex;
  align-items: center;
  gap: 14px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.08);
    border-color: #c9a86c;
  }
`;

const OtaLogo = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
  border-radius: 6px;
  background: #fff;
`;

const OtaMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const OtaName = styled.span`
  font-weight: 600;
`;

const OtaText = styled.span`
  color: #8b7355;
  font-size: 13px;
`;

const QuickInfo = styled.div`
  margin-top: 50px;
  padding-top: 50px;
  border-top: 1px solid #e5e1dc;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  text-align: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const InfoItem = styled.div`
  h4 {
    font-size: 42px;
    font-weight: 300;
    color: #1a1a1a;
    margin-bottom: 8px;

    span {
      color: #c9a86c;
    }
  }

  p {
    font-size: 13px;
    color: #8b7355;
    letter-spacing: 1px;
    text-transform: uppercase;
  }
`;

// The OTA click is the only conversion this page has — the funnel ends here.
// gtag is production-only and credential-gated, so this must stay a no-op when
// it is absent, and it must never block the navigation.
const trackOtaClick = (ota) => {
  window.gtag?.("event", "ota_click", {
    ota_name: ota.name,
    ota_url: ota.link,
  });
};

const BookingWidget = (props) => {
  const { registered_otas: registeredOtas, room_types: roomTypes } =
    usePage().props;
  const { t } = useLandingLanguage();
  const b = t.booking;

  /* Derived from the CMS, not asserted in JSX.
   *
   * These three tiles used to read 50+ / 4.8 / 24/7 as literals in the markup, with only the
   * labels translated. The rating in particular was an unattributed review score presented as
   * fact — it is gone rather than restated, because nothing on this site can substantiate it.
   * The room counts come from the same room_types the Rooms section renders.
   */
  const rooms = (roomTypes || []).reduce(
    (total, room) => total + (Number(room.number_of_rooms) || 0),
    0,
  );

  const stats = [
    rooms > 0 ? { value: String(rooms), label: b.stats.roomsLabel } : null,
    (roomTypes || []).length > 0
      ? { value: String(roomTypes.length), label: b.stats.roomTypesLabel }
      : null,
    { value: b.stats.supportValue, label: b.stats.supportLabel },
  ].filter(Boolean);

  return (
    <BookingSection id="booking">
      <BookingContainer>
        <BookingTitle>{b.subtitle}</BookingTitle>
        <BookingSubtitle>{b.description}</BookingSubtitle>

        {/* Legacy booking form is intentionally disabled for now.
            Keep this section for future in-site booking feature rollout. */}

        <OtaList>
          {(registeredOtas || []).map((ota) => (
            <OtaLink
              key={ota.name}
              href={ota.link}
              target="_blank"
              // Paid referral links to sites that outrank us for our own brand
              // name — sponsored declares the commercial tie, nofollow keeps
              // the equity here. This is permanent; there is no direct booking.
              rel="sponsored nofollow noopener noreferrer"
              onClick={() => trackOtaClick(ota)}
            >
              {ota.logo_url && (
                <OtaLogo src={ota.logo_url} alt={`${ota.name} logo`} />
              )}
              <OtaMeta>
                <OtaName>{ota.name}</OtaName>
                <OtaText>{ota.short_desc}</OtaText>
              </OtaMeta>
            </OtaLink>
          ))}
        </OtaList>

        {stats.length > 0 ? (
          <QuickInfo>
            {stats.map((stat) => (
              <InfoItem key={stat.label}>
                <h3>{stat.value}</h3>
                <p>{stat.label}</p>
              </InfoItem>
            ))}
          </QuickInfo>
        ) : null}
      </BookingContainer>
    </BookingSection>
  );
};

export default BookingWidget;
